<template>
  <Card title="Jouw meerprijzen" subtitle="Overschrijf de prijs van een keuze">
    <p class="section-description">
      Een overschreven prijs is losgekoppeld: die loopt niet meer mee met een nieuwe prijslijst. Vul
      hem in als {{ basisLabel }}. Je inkoopkorting en verkoopafspraak gelden er gewoon overheen.
    </p>

    <p v-if="error" class="catalog-overrides__error">{{ error }}</p>

    <p v-if="loaded && !priceableOptions.length" class="catalog-overrides__empty">
      Dit product heeft geen keuzes met een vaste meerprijs.
    </p>

    <div v-for="option in priceableOptions" :key="option.id" class="catalog-overrides__option">
      <button type="button" class="catalog-overrides__option-head" @click="toggle(option.id)">
        <BaseIcon
          name="chevron"
          size="12"
          class="catalog-overrides__chevron"
          :class="{ 'catalog-overrides__chevron--open': expanded[option.id] }"
        />
        <span class="catalog-overrides__option-label">{{ option.label || option.id }}</span>
        <Label v-if="overrideCount(option)" variant="warning" size="sm">
          {{ overrideCount(option) }} losgekoppeld
        </Label>
      </button>

      <div v-if="expanded[option.id]" class="catalog-overrides__choices">
        <div class="catalog-overrides__row catalog-overrides__row--head">
          <span>Keuze</span>
          <span>Leverancier</span>
          <span>Jouw prijs</span>
          <span />
        </div>
        <div
          v-for="choice in option.priceableChoices"
          :key="choice.id"
          class="catalog-overrides__row"
          :class="{ 'catalog-overrides__row--detached': isOverridden(choice.id) }"
        >
          <span class="catalog-overrides__choice-label">{{ choice.label || choice.id }}</span>
          <span class="catalog-overrides__master">{{ formatCurrency(choice.price) }}</span>
          <input
            :id="`override-${choice.id}`"
            v-model="drafts[choice.id]"
            class="catalog-overrides__input"
            type="number"
            min="0"
            step="0.01"
            :placeholder="String(choice.price)"
          />
          <Btn
            v-if="isOverridden(choice.id)"
            variant="plain"
            size="sm"
            @click="drafts[choice.id] = ''"
          >
            Terug naar leverancier
          </Btn>
          <span v-else />
        </div>
      </div>
    </div>

    <div class="catalog-overrides__footer">
      <Btn variant="primary" :loading="saving" :disabled="!loaded || !dirty" @click="save">
        Meerprijzen opslaan
      </Btn>
    </div>
  </Card>
</template>

<script>
  import { computed, onMounted, reactive, ref } from 'vue';
  import Card from '@/components/partials/Card.vue';
  import Btn from '@/components/partials/Btn.vue';
  import Label from '@/components/partials/Label.vue';
  import BaseIcon from '@/components/partials/BaseIcon.vue';
  import { PRICE_BASIS_LABELS } from '@/components/products/CatalogAgreementFields.vue';
  import { catalogService } from '@/services/catalogService';
  import { useToastStore } from '@/store/toastStore';
  import { formatCurrency } from '@/utils/currency';

  const isBlank = (value) => value === '' || value === null || value === undefined;

  export default {
    name: 'CatalogPriceOverridesCard',
    components: { Card, Btn, Label, BaseIcon },
    props: {
      installationId: { type: String, required: true },
    },
    emits: ['saved'],
    setup(props, { emit }) {
      const toastStore = useToastStore();
      const loaded = ref(false);
      const saving = ref(false);
      const error = ref('');
      const options = ref([]);
      const priceBasis = ref('brochure_excl_vat');
      const saved = reactive({});
      const drafts = reactive({});
      const expanded = reactive({});

      const basisLabel = computed(() =>
        (PRICE_BASIS_LABELS[priceBasis.value] || 'brochureprijs').toLowerCase()
      );

      const priceableOptions = computed(() =>
        options.value
          .filter((option) => option.enabled)
          .map((option) => ({
            ...option,
            priceableChoices: (option.choices || []).filter(
              (choice) => choice.enabled && !choice.price_formula
            ),
          }))
          .filter((option) => option.priceableChoices.length > 0)
      );

      const isOverridden = (choiceId) => !isBlank(drafts[choiceId]);
      const overrideCount = (option) =>
        option.priceableChoices.filter((choice) => isOverridden(choice.id)).length;

      const dirty = computed(() =>
        Object.keys(drafts).some((id) => {
          const draft = isBlank(drafts[id]) ? null : Number(drafts[id]);
          return draft !== saved[id];
        })
      );

      const applyDealer = (dealer) => {
        options.value = dealer?.options || [];
        priceBasis.value = dealer?.price_basis || 'brochure_excl_vat';
        for (const option of options.value) {
          let hasOverride = false;
          for (const choice of option.choices || []) {
            const value = choice.override_price ?? null;
            saved[choice.id] = value;
            drafts[choice.id] = value === null ? '' : value;
            if (value !== null) hasOverride = true;
          }
          if (expanded[option.id] === undefined) expanded[option.id] = hasOverride;
        }
      };

      const load = async () => {
        error.value = '';
        try {
          const result = await catalogService.getInstallation(props.installationId);
          applyDealer(result.dealer);
          loaded.value = true;
        } catch (err) {
          error.value = err?.response?.data?.error || 'Meerprijzen laden mislukt';
        }
      };

      const toggle = (optionId) => {
        expanded[optionId] = !expanded[optionId];
      };

      const save = async () => {
        const invalid = Object.keys(drafts).find(
          (id) => !isBlank(drafts[id]) && !(Number(drafts[id]) >= 0)
        );
        if (invalid) {
          error.value = 'Een prijs moet 0 of hoger zijn';
          return;
        }
        saving.value = true;
        error.value = '';
        try {
          const choiceOverrides = Object.keys(drafts)
            .filter((id) => !isBlank(drafts[id]))
            .map((id) => ({ scope: 'choice', id, price: Number(drafts[id]) }));
          const result = await catalogService.updatePriceOverrides(
            props.installationId,
            (current) => [...current.filter((item) => item?.scope !== 'choice'), ...choiceOverrides]
          );
          applyDealer(result.dealer);
          toastStore.addToast({ type: 'success', message: 'Meerprijzen opgeslagen' });
          emit('saved', result);
        } catch (err) {
          const details = err?.response?.data?.details;
          error.value =
            (Array.isArray(details) && details[0]?.message) ||
            err?.response?.data?.error ||
            'Opslaan mislukt';
        } finally {
          saving.value = false;
        }
      };

      onMounted(load);

      return {
        loaded,
        saving,
        error,
        basisLabel,
        priceableOptions,
        drafts,
        expanded,
        dirty,
        isOverridden,
        overrideCount,
        toggle,
        save,
        formatCurrency,
        reload: load,
      };
    },
  };
</script>

<style scoped lang="scss">
  .catalog-overrides {
    &__option {
      border-top: 1px solid var(--slate-100);

      &:first-of-type {
        border-top: 0;
      }
    }

    &__option-head {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      width: 100%;
      padding: 0.6rem 0;
      border: 0;
      background: none;
      color: var(--slate-800);
      font-weight: 500;
      text-align: left;
      cursor: pointer;
    }

    &__option-label {
      flex: 1;
    }

    &__chevron {
      color: var(--slate-500);
      transform: rotate(90deg);
      transition: transform 0.15s ease;

      &--open {
        transform: rotate(180deg);
      }
    }

    &__choices {
      padding: 0 0 0.75rem 1.4rem;
    }

    &__row {
      display: grid;
      grid-template-columns: minmax(0, 2fr) 7rem 8rem minmax(0, 1.4fr);
      gap: 0.75rem;
      align-items: center;
      padding: 0.3rem 0;
      @include fontSize('sm');

      &--head {
        color: var(--slate-500);
        font-weight: 500;
      }

      &--detached .catalog-overrides__master {
        text-decoration: line-through;
        color: var(--slate-400);
      }

      @include sm-down {
        grid-template-columns: minmax(0, 1fr) 6rem 7rem;

        > :last-child {
          grid-column: 1 / -1;
        }
      }
    }

    &__choice-label {
      @include ellipsis(1);
    }

    &__master {
      color: var(--slate-600);
    }

    &__input {
      width: 100%;
      padding: 0.35rem 0.5rem;
      border: 1px solid var(--slate-300);
      border-radius: 0.375rem;
      background: white;
      @include fontSize('sm');

      &:focus {
        outline: none;
        border-color: var(--brand-500);
      }
    }

    &__empty {
      margin: 0;
      color: var(--slate-500);
    }

    &__error {
      margin: 0 0 1rem;
      color: var(--red-700);
    }

    &__footer {
      display: flex;
      justify-content: flex-end;
      margin-top: 1rem;
    }
  }
</style>
